import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, message, Card, Tag, Select, Alert, Upload, Spin, Tooltip, Image, Descriptions, Row, Col } from 'antd';
import { CopyOutlined, ArrowLeftOutlined, UserOutlined, EditOutlined, UploadOutlined, InboxOutlined, WarningOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import authService from '../utils/auth';
import { getCountryDisplay, getAllCountries } from '../utils/countryCode';
import { validatePassportName, getNameFormatHint } from '../utils/nameValidator';
import ChinaDocAction from '../components/ChinaDocAction';
import PassportReuseTag from '../components/PassportReuseTag';
import './SalesTourists.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { Option } = Select;
const { Dragger } = Upload;

// 证件类型显示映射
const DOCUMENT_TYPE_LABEL = {
  passport: '护照',
  nz_driver_license: 'NZ驾照',
  nz_senior_card: 'NZ老年卡',
};

// 判断某个游客记录是否属于 daytour
function isDaytour(record) {
  // tour 可能是对象（populated）或 tourType 直接挂在 record 上
  const tourType =
    record?.tour?.tourType ||
    record?.tourType ||
    (typeof record?.tourId === 'object' ? record?.tourId?.tourType : null);
  return tourType === 'daytour';
}

// 中国团附加证件的标签与「补传」单元格统一在 components/ChinaDocAction 里实现

function SalesTourists() {
  const [tourists, setTourists] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [editingTourist, setEditingTourist] = useState(null);
  const [uploadingTourist, setUploadingTourist] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [departureDateFilter, setDepartureDateFilter] = useState('all');
  const [tourTypeFilter, setTourTypeFilter] = useState('all');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const [recognizedData, setRecognizedData] = useState(null);
  const [passportModalVisible, setPassportModalVisible] = useState(false);
  const [passportModalRecord, setPassportModalRecord] = useState(null);
  // daytour 上传流程用的证件类型选择
  const [selectedDocumentType, setSelectedDocumentType] = useState('passport');
  // 添加游客弹窗中选中的产品（用于判断是否 daytour）
  const [addTourSelectedId, setAddTourSelectedId] = useState(null);
  const [ekokWarning, setEkokWarning] = useState('');
  // 标准团护照上传核对流程（识别→核对/补齐→提交）
  const [passportConfirmVisible, setPassportConfirmVisible] = useState(false);
  const [passportTempFile, setPassportTempFile] = useState(null);
  const [passportTempImageUrl, setPassportTempImageUrl] = useState(null);
  const [passportNameMismatch, setPassportNameMismatch] = useState('');
  const [passportExpiryWarning, setPassportExpiryWarning] = useState(null);
  const [passportOcrFailed, setPassportOcrFailed] = useState('');
  const [confirmingPassport, setConfirmingPassport] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passportForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = authService.getUserInfo();

  useEffect(() => {
    fetchTours();
    fetchMyTourists();

    // 如果从旅游产品页面跳转过来，设置选中的产品
    if (location.state?.selectedTour) {
      setSelectedTour(location.state.selectedTour);
    }
  }, [location.state]);

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${API_BASE}/tours`, {
        headers: authService.getAuthHeaders()
      });
      setTours(response.data.data);
    } catch (error) {
      message.error('获取旅游产品失败');
    }
  };

  const fetchMyTourists = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tourists`, {
        headers: authService.getAuthHeaders()
      });
      // 筛选出当前销售人员的游客：salespersonId 关联为主，salesName 兜底（同步游客 salesName 是 broker 原文，须靠 id）
      const myTourists = response.data.data.filter(tourist =>
        tourist.salespersonId === userInfo?.id || tourist.salesName === userInfo?.name
      );
      setTourists(myTourists);
    } catch (error) {
      message.error('获取游客列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 根据 tourId 从已加载的 tours 列表中查找该产品是否为 daytour
  const getTourTypeById = (tourId) => {
    const id = typeof tourId === 'object' ? tourId?.id : tourId;
    const found = tours.find(t => String(t.id) === String(id));
    return found?.tourType || null;
  };

  // 根据 tourId 获取上车点列表
  const getPickupPointsByTourId = (tourId) => {
    const id = typeof tourId === 'object' ? tourId?.id : tourId;
    const found = tours.find(t => String(t.id) === String(id));
    if (!found || !found.pickupPoints) return [];
    return found.pickupPoints.map(p => typeof p === 'string' ? { name: p, time: '' } : p);
  };

  const handleAddTourist = async (values) => {
    // 验证姓名格式
    const nameValidation = validatePassportName(values.touristName);
    if (!nameValidation.valid) {
      message.error(nameValidation.error);
      return;
    }

    try {
      const payload = {
        tourId: values.tourId,
        touristName: nameValidation.formatted,
        salesName: userInfo.name,
        salespersonId: userInfo.id
      };

      // 额外字段
      const tourType = getTourTypeById(values.tourId);
      if (values.ekok) payload.ekok = values.ekok;
      if (tourType !== 'daytour' && values.contactPhone?.trim()) payload.contactPhone = values.contactPhone.trim();
      if (tourType === 'daytour') {
        if (values.phone) payload.phone = values.phone;
        if (values.pickupPoint) payload.pickupPoint = values.pickupPoint;
      }

      const response = await axios.post(`${API_BASE}/tourists`, payload, {
        headers: authService.getAuthHeaders()
      });

      const uploadUrl = response.data.uploadUrl;
      const reused = response.data.reusedPassport;
      if (reused) {
        // 沿用了历史护照：不再弹上传链接
        Modal.success({
          title: '已沿用历史护照',
          content: (
            <div style={{ lineHeight: 1.8 }}>
              <div>游客 {nameValidation.formatted} 已添加，护照信息取自历史订单，无需重新上传。</div>
              <div>来源订单：{reused.fromTourName || '-'}{reused.fromDepartureDate ? `（${reused.fromDepartureDate} 出团）` : ''}</div>
              <div>护照号码：{reused.passportNumber}</div>
              <div>有效期至：{reused.passportExpiryDate}</div>
            </div>
          ),
        });
        form.resetFields();
        setAddTourSelectedId(null);
        setModalVisible(false);
        setSelectedTour(null);
        fetchMyTourists();
        return;
      }
      message.success('游客添加成功');

      Modal.info({
        title: '上传链接已生成',
        width: 600,
        content: (
          <div>
            <p>请将以下链接发送给游客 <strong>{nameValidation.formatted}</strong>：</p>
            <Input.TextArea
              value={uploadUrl}
              readOnly
              autoSize={{ minRows: 3, maxRows: 5 }}
              style={{ marginBottom: 12 }}
            />
            <Button
              type="primary"
              onClick={async () => {
                try {
                  if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(uploadUrl);
                    message.success('链接已复制到剪贴板');
                  } else {
                    const textArea = document.createElement('textarea');
                    textArea.value = uploadUrl;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();

                    const successful = document.execCommand('copy');
                    document.body.removeChild(textArea);

                    if (successful) {
                      message.success('链接已复制到剪贴板');
                    } else {
                      message.error('复制失败，请手动复制上方文本框中的链接');
                    }
                  }
                } catch (err) {
                  console.error('Copy failed:', err);
                  message.error('复制失败，请手动复制上方文本框中的链接');
                }
              }}
              icon={<CopyOutlined />}
              block
            >
              复制链接
            </Button>
            <Alert
              message="使用说明"
              description={
                tourType === 'daytour'
                  ? '游客点击链接后可以上传证件照片，系统会自动识别证件信息并要求游客确认。'
                  : '游客点击链接后可以上传护照照片，系统会自动识别护照信息并要求游客确认。'
              }
              type="info"
              showIcon
              style={{ marginTop: 12 }}
            />
          </div>
        ),
        okText: '确定'
      });

      form.resetFields();
      setAddTourSelectedId(null);
      setModalVisible(false);
      setSelectedTour(null);
      fetchMyTourists();
    } catch (error) {
      message.error(error.response?.data?.error || '添加游客失败');
    }
  };

  const handleEditTourist = (tourist) => {
    setEditingTourist(tourist);
    setEkokWarning('');
    const tourIdValue = typeof tourist.tourId === 'object' ? tourist.tourId.id : tourist.tourId;
    editForm.setFieldsValue({
      touristName: tourist.touristName,
      tourId: tourIdValue,
      ekok: tourist.ekok,
      phone: tourist.phone,
      pickupPoint: tourist.pickupPoint
    });
    setEditModalVisible(true);
  };

  const handleUpdateTourist = async (values) => {
    const nameValidation = validatePassportName(values.touristName);
    if (!nameValidation.valid) {
      message.error(nameValidation.error);
      return;
    }

    try {
      const updatePayload = {
        tourId: values.tourId
      };
      // 已确认状态不允许修改姓名
      if (editingTourist?.orderStatus !== 'confirmed') {
        updatePayload.touristName = nameValidation.formatted;
      }

      // ekok 字段（出境游和一日游都有）
      if (values.ekok !== undefined) updatePayload.ekok = values.ekok;
      // daytour 额外字段
      if (isDaytour(editingTourist)) {
        if (values.phone !== undefined) updatePayload.phone = values.phone;
        // 上车点下拉框只在团配了可选上车点时渲染；没渲染时表单里没有这个值，
        // 照样写 null 会把会员系统同步来的上车点清空（2026-10-21 教堂湾 10-24 团两人即此）
        if (getPickupPointsByTourId(editingTourist.tourId).length > 0) {
          updatePayload.pickupPoint = values.pickupPoint || null;
        }
      }

      await axios.put(`${API_BASE}/tourists/${editingTourist.id}`, updatePayload, {
        headers: authService.getAuthHeaders()
      });

      message.success('游客信息更新成功');
      editForm.resetFields();
      setEditModalVisible(false);
      setEditingTourist(null);
      fetchMyTourists();
    } catch (error) {
      message.error(error.response?.data?.error || '更新失败');
    }
  };

  const handleDeleteTourist = async (touristId) => {
    try {
      await axios.delete(`${API_BASE}/tourists/${touristId}`, {
        headers: authService.getAuthHeaders()
      });
      message.success('游客删除成功');
      fetchMyTourists();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleUploadPassport = (tourist) => {
    setUploadingTourist(tourist);
    setRecognizedData(null);
    // 重置证件类型选择（daytour 默认为护照）
    setSelectedDocumentType('passport');
    setUploadModalVisible(true);
  };

  // ── 标准护照上传（非 daytour）────────────────────────────────────────────
  // 标准团护照上传：先预览识别（不落库），销售核对/补齐必填信息后才真正提交保存
  const handlePassportUpload = async (file) => {
    setUploadingPassport(true);

    const reader = new FileReader();
    reader.onload = (e) => setPassportTempImageUrl(e.target.result);
    reader.readAsDataURL(file);
    setPassportTempFile(file);

    const formData = new FormData();
    formData.append('passport', file);
    formData.append('preview', 'true');

    try {
      const response = await axios.post(
        `${API_BASE}/tourists/${uploadingTourist.id}/update-passport-preview`,
        formData,
        {
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // OCR 禁用 / 识别失败：照片已保存，进入全手动录入（同样必须补齐才能提交）
      if (response.data.ocrDisabled) {
        setPassportNameMismatch('');
        setPassportExpiryWarning(null);
        setPassportOcrFailed(response.data.message || '自动识别不可用，请对照护照照片手动填写全部信息');
        passportForm.setFieldsValue({
          fullName: uploadingTourist?.touristName || '',
          passportNumber: '',
          gender: 'M',
          nationality: 'CHN',
          birthDate: '',
          issueDate: '',
          expiryDate: '',
          birthPlace: '',
        });
        setUploadModalVisible(false);
        setPassportConfirmVisible(true);
        return false;
      }

      if (response.data.success) {
        const d = response.data.data;
        setPassportOcrFailed('');
        let nationality = d.nationality || 'CHN';
        if (nationality === 'CHI') nationality = 'CHN';

        const formValues = {
          fullName: (d.recognizedName || d.fullName || '').toUpperCase(),
          passportNumber: (d.passportNumber || '').toUpperCase(),
          gender: d.gender || 'M',
          nationality,
          birthDate: d.birthDate || '',
          issueDate: d.issueDate || '',
          expiryDate: d.expiryDate || '',
          birthPlace: d.birthPlace || '',
        };

        // 姓名不一致：保留录入姓名，弹窗内红字提示
        const ocrName = formValues.fullName.replace(/\s+/g, '');
        const originalName = (uploadingTourist?.touristName || '').toUpperCase().replace(/\s+/g, '');
        if (ocrName && originalName && ocrName !== originalName) {
          setPassportNameMismatch(`护照识别姓名：${formValues.fullName}`);
          formValues.fullName = uploadingTourist.touristName;
        } else {
          setPassportNameMismatch('');
        }

        passportForm.setFieldsValue(formValues);
        checkConfirmExpiryWarning(formValues.expiryDate);
        setUploadModalVisible(false);
        setPassportConfirmVisible(true);
        message.success('护照识别完成，请核对信息，缺失项补齐后才能提交');
      }
    } catch (error) {
      if (error.response?.data?.error === 'INCOMPLETE_PASSPORT' || error.response?.data?.error === 'MRZ_UNVERIFIED') {
        message.error(error.response.data.message || '护照照片未拍全，请把护照信息页所有信息和底部两行机读码（MRZ）完整拍入、无裁切遮挡后重新上传', 8);
      } else if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('护照识别失败，请重试');
      }
      setPassportTempFile(null);
      setPassportTempImageUrl(null);
    } finally {
      setUploadingPassport(false);
    }

    return false;
  };

  // 护照有效期距出团日期不足6个月提醒（核对表单里实时显示）
  const checkConfirmExpiryWarning = (expiryStr) => {
    if (!expiryStr || !uploadingTourist?.departureDate) {
      setPassportExpiryWarning(null);
      return;
    }
    const expiryDate = moment(expiryStr, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
    const departureDate = moment(uploadingTourist.departureDate);
    if (!expiryDate.isValid() || !departureDate.isValid()) {
      setPassportExpiryWarning(null);
      return;
    }
    const monthsDiff = expiryDate.diff(departureDate, 'months', true);
    setPassportExpiryWarning(monthsDiff < 6 ? {
      expiryDate: expiryDate.format('YYYY-MM-DD'),
      departureDate: departureDate.format('YYYY-MM-DD'),
      monthsDiff: Math.max(0, monthsDiff).toFixed(1)
    } : null);
  };

  // 校验 OCR 日期字段格式（DD/MM/YYYY 或 YYYY-MM-DD）
  const validateOcrDate = (_, value) => {
    if (!value) return Promise.resolve();
    const m = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
    return m.isValid() ? Promise.resolve() : Promise.reject(new Error('日期格式应为 DD/MM/YYYY'));
  };

  const closePassportConfirm = () => {
    setPassportConfirmVisible(false);
    setPassportTempFile(null);
    setPassportTempImageUrl(null);
    setPassportNameMismatch('');
    setPassportExpiryWarning(null);
    setPassportOcrFailed('');
    setUploadingTourist(null);
    passportForm.resetFields();
  };

  const handleCancelPassportConfirm = () => {
    Modal.confirm({
      title: '放弃本次上传？',
      content: '护照信息尚未提交，放弃后本次识别结果不会保存。',
      okText: '放弃',
      cancelText: '继续核对',
      onOk: closePassportConfirm,
    });
  };

  // 核对表单确认提交：必填项齐全才真正保存
  const handleConfirmPassportSubmit = async () => {
    let values;
    try {
      values = await passportForm.validateFields();
    } catch (e) {
      message.error('请补齐所有必填项后再提交');
      return;
    }
    const nameValidation = validatePassportName(values.fullName);
    if (!nameValidation.valid) {
      message.error(nameValidation.error);
      return;
    }
    if (!passportTempFile) {
      message.error('护照图片丢失，请重新上传');
      closePassportConfirm();
      return;
    }

    setConfirmingPassport(true);
    try {
      const formData = new FormData();
      formData.append('passport', passportTempFile);
      formData.append('confirmData', JSON.stringify({
        fullName: nameValidation.formatted,
        passportNumber: (values.passportNumber || '').toUpperCase().trim(),
        gender: values.gender,
        issueDate: values.issueDate,
        expiryDate: values.expiryDate,
        birthDate: values.birthDate,
        birthPlace: values.birthPlace,
        nationality: values.nationality
      }));
      const response = await axios.post(
        `${API_BASE}/tourists/${uploadingTourist.id}/update-passport`,
        formData,
        { headers: { ...authService.getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data.success) {
        message.success('护照信息已确认并保存');
        closePassportConfirm();
        fetchMyTourists();
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.response?.data?.error || '提交失败，请重试');
    } finally {
      setConfirmingPassport(false);
    }
  };

  // ── Daytour 证件上传（直接保存，不做OCR识别）────────────────────────────
  const handleDaytourDocumentUpload = async (file) => {
    setUploadingPassport(true);

    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', selectedDocumentType);

    try {
      const response = await axios.post(
        `${API_BASE}/tourists/${uploadingTourist.id}/update-document`,
        formData,
        {
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        message.success(response.data.recognizedName
          ? `证件已上传，姓名已识别：${response.data.recognizedName}`
          : '证件已上传');
        fetchMyTourists();
        setUploadModalVisible(false);
        setUploadingTourist(null);
      } else {
        message.error(response.data.error || '证件上传失败，请重试');
      }
    } catch (error) {
      message.error(error.response?.data?.error || '证件上传失败，请重试');
    } finally {
      setUploadingPassport(false);
    }

    return false;
  };

  const copyUploadLink = async (uploadLink) => {
    const fullUrl = `${window.location.origin}/upload/${uploadLink}`;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(fullUrl);
        message.success('上传链接已复制');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          if (successful) {
            message.success('上传链接已复制');
          } else {
            message.error('复制失败，请手动复制');
          }
        } catch (err) {
          console.error('Fallback copy failed:', err);
          message.error('复制失败，请手动复制');
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
      Modal.info({
        title: '上传链接',
        content: (
          <div>
            <p>请手动复制以下链接：</p>
            <Input.TextArea
              value={fullUrl}
              readOnly
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
          </div>
        ),
        okText: '确定'
      });
    }
  };

  // ── 动态列定义 ─────────────────────────────────────────────────────────────
  const buildColumns = (touristList) => {
    return [
      {
        title: '护照姓名',
        key: 'name',
        width: 150,
        ellipsis: {
          showTitle: false,
        },
        render: (_, record) => {
          const displayName = record.touristName || record.passportName;
          let expiryWarning = false;
          if (record.passportExpiryDate && record.departureDate) {
            const expiry = moment(record.passportExpiryDate);
            const departure = moment(record.departureDate);
            if (expiry.isValid() && departure.isValid()) {
              const monthsDiff = expiry.diff(departure, 'months', true);
              if (monthsDiff < 6) {
                expiryWarning = true;
              }
            }
          }
          // 出境游：护照识别姓名与录入姓名不一致时红色提示
          const nameMismatch = !isDaytour(record) &&
            record.passportName && record.touristName &&
            record.passportName.trim().toUpperCase() !== record.touristName.trim().toUpperCase();
          return (
            <div>
              <Tooltip placement="topLeft" title={displayName}>
                {displayName}
                {expiryWarning && (
                  <Tooltip title={`护照有效期不足6个月（${moment(record.passportExpiryDate).format('YYYY-MM-DD')}）`}>
                    <WarningOutlined style={{ color: '#fa8c16', marginLeft: 6, fontSize: 14 }} />
                  </Tooltip>
                )}
              </Tooltip>
              {nameMismatch && (
                <div style={{ color: '#ff4d4f', fontSize: 12, lineHeight: '18px' }}>
                  护照姓名：{record.passportName}
                </div>
              )}
              {record.passportReuse && (
                <div><PassportReuseTag reuse={record.passportReuse} /></div>
              )}
            </div>
          );
        },
      },
      {
        title: '团号',
        dataIndex: 'tourName',
        key: 'tourName',
        width: 150,
        ellipsis: true,
      },
      {
        title: '产品',
        dataIndex: 'destinationName',
        key: 'destinationName',
        width: 100,
        render: (name) => name ? <Tag color="purple">{name}</Tag> : <span style={{ color: '#999' }}>-</span>,
      },
      {
        title: '团类型',
        key: 'tourType',
        width: 90,
        render: (_, record) => {
          const dt = isDaytour(record);
          return dt
            ? <Tag color="orange">一日游</Tag>
            : <Tag color="blue">出境游</Tag>;
        },
      },
      {
        title: '出团日期',
        dataIndex: 'departureDate',
        key: 'departureDate',
        width: 110,
        sorter: (a, b) => {
          if (!a.departureDate && !b.departureDate) return 0;
          if (!a.departureDate) return 1;
          if (!b.departureDate) return -1;
          return moment(a.departureDate).valueOf() - moment(b.departureDate).valueOf();
        },
        sortDirections: ['ascend', 'descend'],
        defaultSortOrder: 'ascend',
        render: (date) => date ? moment(date).format('YYYY-MM-DD') : '-',
      },
      {
        // 中国团附加证件：中国护照要身份证、台湾护照要台胞证（机加酒产品不要求）
        title: '身份证/台胞证',
        key: 'chinaDoc',
        width: 140,
        // 护照传完后这里直接出「补传身份证/台胞证」按钮，已传的可查看/重传（组件内统一实现）
        render: (_, record) => <ChinaDocAction record={record} onDone={fetchMyTourists} />,
      },
      {
        title: '添加时间',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 100,
        render: (date) => moment(date).format('MM-DD'),
      },
      {
        title: '操作',
        key: 'action',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Button
            size="small"
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setDetailRecord(record);
              setDetailModalVisible(true);
            }}
          >
            查看详情
          </Button>
        ),
      },
    ];
  };

  // 根据条件过滤游客
  const filteredTourists = tourists.filter(tourist => {
    const tourFilter = selectedTour ? tourist.tourName === selectedTour.productName : true;

    const searchFilter = searchText === '' ||
      tourist.touristName?.toLowerCase().includes(searchText.toLowerCase()) ||
      tourist.passportName?.toLowerCase().includes(searchText.toLowerCase()) ||
      tourist.passportNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
      tourist.documentNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
      tourist.ekok?.toLowerCase().includes(searchText.toLowerCase());

    const dateFilter = departureDateFilter === 'all' ||
      (tourist.departureDate && moment(tourist.departureDate).format('YYYY-MM-DD') === departureDateFilter);

    const tourTypeResult = tourTypeFilter === 'all' ||
      (tourTypeFilter === 'daytour' ? isDaytour(tourist) : !isDaytour(tourist));

    return tourFilter && searchFilter && dateFilter && tourTypeResult;
  });

  const columns = buildColumns(filteredTourists);

  // 当前正在上传的游客是否为 daytour
  const uploadingIsDaytour = uploadingTourist ? isDaytour(uploadingTourist) : false;

  // 添加游客弹窗中选中产品是否为 daytour
  const addTourIsDaytour = addTourSelectedId
    ? getTourTypeById(addTourSelectedId) === 'daytour'
    : false;

  // 添加游客弹窗中选中产品是否为出境游
  const addTourIsOutbound = addTourSelectedId
    ? getTourTypeById(addTourSelectedId) !== 'daytour'
    : false;

  return (
    <div className="sales-tourists">
      <Card
        title={
          <div className="page-header">
            <div className="header-left">
              <h3>我的游客</h3>
            </div>
            <div className="header-right">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/sales/tours')}
              >
                返回产品列表
              </Button>
            </div>
          </div>
        }
        className="tourists-card"
      >

        <div className="action-bar">
          <div className="filter-controls">
            <Input.Search
              placeholder="搜索护照姓名/护照号/EKOK"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200, marginRight: 8 }}
              allowClear
            />
            <Select
              value={departureDateFilter}
              onChange={setDepartureDateFilter}
              style={{ width: 150, marginRight: 8 }}
            >
              <Option value="all">所有日期</Option>
              {[...new Set(tourists.map(t => t.departureDate).filter(Boolean))]
                .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
                .map(date => {
                  const formatted = moment(date).format('YYYY-MM-DD');
                  return <Option key={formatted} value={formatted}>{formatted}</Option>;
                })
              }
            </Select>
            <Select
              value={tourTypeFilter}
              onChange={setTourTypeFilter}
              style={{ width: 120, marginRight: 8 }}
            >
              <Option value="all">所有类型</Option>
              <Option value="standard">出境游</Option>
              <Option value="daytour">一日游</Option>
            </Select>
          </div>

          <div className="stats-info">
            <span>总计：{filteredTourists.length} 人</span>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filteredTourists}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          size="small"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 个游客`,
          }}
        />
      </Card>

      {/* 添加游客弹窗 */}
      <Modal
        title="添加游客"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setAddTourSelectedId(null);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddTourist}
        >
          <Form.Item
            name="tourId"
            label="选择旅游产品"
            rules={[{ required: true, message: '请选择旅游产品' }]}
          >
            <Select
              placeholder="请选择旅游产品"
              showSearch
              onChange={(val) => setAddTourSelectedId(val)}
            >
              {tours.map(tour => (
                <Option key={tour.id} value={tour.id}>
                  {tour.productName} - {moment(tour.departureDate).format('YYYY-MM-DD')}
                  {tour.tourType === 'daytour' && (
                    <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>一日游</Tag>
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="touristName"
            label="游客姓名"
            rules={[
              { required: true, message: '请输入游客姓名' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const validation = validatePassportName(value);
                  if (validation.valid) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(validation.error));
                }
              }
            ]}
            tooltip={getNameFormatHint()}
            extra="格式：姓/名（纯英文），例如：ZHANG/SAN"
          >
            <Input
              placeholder="姓/名 (例: ZHANG/SAN)"
              style={{ textTransform: 'uppercase' }}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                form.setFieldsValue({ touristName: value });
              }}
            />
          </Form.Item>

          {/* 出境游 EKOK 字段 */}
          {addTourIsOutbound && (
            <Form.Item
              name="ekok"
              label="EKOK编号"
              rules={[{ required: true, message: '请输入EKOK编号（出境游必填）' }]}
              validateStatus={ekokWarning ? 'warning' : undefined}
              help={ekokWarning || undefined}
            >
              <Input placeholder="请输入EKOK编号"
                onBlur={async (e) => {
                  const val = e.target.value?.trim();
                  if (!val || !addTourSelectedId) { setEkokWarning(''); return; }
                  try {
                    const resp = await axios.get(`${API_BASE}/tourists/check-ekok`, {
                      params: { tourId: addTourSelectedId, ekok: val },
                      headers: authService.getAuthHeaders()
                    });
                    setEkokWarning(resp.data.exists ? `该EKOK已存在（${resp.data.touristName}）` : '');
                  } catch { setEkokWarning(''); }
                }}
              />
            </Form.Item>
          )}
          {addTourIsOutbound && (
            <Form.Item
              name="contactPhone"
              label="联系电话"
              normalize={v => v ? v.replace(/\s/g, '') : v}
            >
              <Input placeholder="选填，例如 0211234567" inputMode="tel" />
            </Form.Item>
          )}

          {/* Daytour 额外字段 */}
          {addTourIsDaytour && (
            <>
              <Form.Item
                name="ekok"
                label="EKOK编号"
                rules={[{ required: true, message: '请输入EKOK编号（一日游必填）' }]}
                validateStatus={ekokWarning ? 'warning' : undefined}
                help={ekokWarning || undefined}
              >
                <Input placeholder="请输入EKOK编号"
                  onBlur={async (e) => {
                    const val = e.target.value?.trim();
                    if (!val || !addTourSelectedId) { setEkokWarning(''); return; }
                    try {
                      const resp = await axios.get(`${API_BASE}/tourists/check-ekok`, {
                        params: { tourId: addTourSelectedId, ekok: val },
                        headers: authService.getAuthHeaders()
                      });
                      setEkokWarning(resp.data.exists ? `该EKOK已存在（${resp.data.touristName}）` : '');
                    } catch { setEkokWarning(''); }
                  }}
                />
              </Form.Item>

              <Form.Item
                name="phone"
                label="联系电话"
                normalize={v => v ? v.replace(/\s/g, '') : v}
              >
                <Input placeholder="请输入联系电话（可选）" />
              </Form.Item>

              {getPickupPointsByTourId(addTourSelectedId).length > 0 && (
                <Form.Item
                  name="pickupPoint"
                  label="上车点"
                >
                  <Select placeholder="请选择上车点（可选）" allowClear>
                    {getPickupPointsByTourId(addTourSelectedId).map((p, i) => (
                      <Option key={i} value={p.name}>
                        {p.name}{p.time ? ` (${p.time})` : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<UserOutlined />}>
                添加游客
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setAddTourSelectedId(null);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑游客弹窗 */}
      <Modal
        title="编辑游客信息"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingTourist(null);
          editForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateTourist}
        >
          <Form.Item
            name="tourId"
            label="旅游产品"
            rules={[{ required: true, message: '请选择旅游产品' }]}
          >
            <Select placeholder="请选择旅游产品" showSearch>
              {tours.map(tour => (
                <Option key={tour.id} value={tour.id}>
                  {tour.productName} - {moment(tour.departureDate).format('YYYY-MM-DD')}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="touristName"
            label="游客姓名"
            rules={[
              { required: true, message: '请输入游客姓名' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const validation = validatePassportName(value);
                  if (validation.valid) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(validation.error));
                }
              }
            ]}
            tooltip={getNameFormatHint()}
            extra={editingTourist?.orderStatus === 'confirmed' ? '已确认状态，姓名不可修改' : '格式：姓/名（纯英文），例如：ZHANG/SAN'}
          >
            <Input
              placeholder="姓/名 (例: ZHANG/SAN)"
              style={{ textTransform: 'uppercase' }}
              disabled={editingTourist?.orderStatus === 'confirmed'}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                editForm.setFieldsValue({ touristName: value });
              }}
            />
          </Form.Item>

          {/* 出境游编辑 EKOK 字段 */}
          {editingTourist && !isDaytour(editingTourist) && (
            <Form.Item
              name="ekok"
              label="EKOK编号"
              rules={[{ required: true, message: '请输入EKOK编号（出境游必填）' }]}
              validateStatus={ekokWarning ? 'warning' : undefined}
              help={ekokWarning || undefined}
            >
              <Input placeholder="请输入EKOK编号"
                onBlur={async (e) => {
                  const val = e.target.value?.trim();
                  if (!val || !editingTourist?.tourId) { setEkokWarning(''); return; }
                  try {
                    const resp = await axios.get(`${API_BASE}/tourists/check-ekok`, {
                      params: { tourId: editingTourist.tourId, ekok: val, touristId: editingTourist.id },
                      headers: authService.getAuthHeaders()
                    });
                    setEkokWarning(resp.data.exists ? `该EKOK已存在（${resp.data.touristName}）` : '');
                  } catch { setEkokWarning(''); }
                }}
              />
            </Form.Item>
          )}

          {/* Daytour 编辑字段 */}
          {editingTourist && isDaytour(editingTourist) && (
            <>
              <Form.Item
                name="ekok"
                label="EKOK编号"
                validateStatus={ekokWarning ? 'warning' : undefined}
                help={ekokWarning || undefined}
              >
                <Input placeholder="请输入EKOK编号"
                  onBlur={async (e) => {
                    const val = e.target.value?.trim();
                    if (!val || !editingTourist?.tourId) { setEkokWarning(''); return; }
                    try {
                      const resp = await axios.get(`${API_BASE}/tourists/check-ekok`, {
                        params: { tourId: editingTourist.tourId, ekok: val, touristId: editingTourist.id },
                        headers: authService.getAuthHeaders()
                      });
                      setEkokWarning(resp.data.exists ? `该EKOK已存在（${resp.data.touristName}）` : '');
                    } catch { setEkokWarning(''); }
                  }}
                />
              </Form.Item>

              <Form.Item
                name="phone"
                label="联系电话"
                normalize={v => v ? v.replace(/\s/g, '') : v}
              >
                <Input placeholder="请输入联系电话（可选）" />
              </Form.Item>

              {(() => {
                const tourIdValue = typeof editingTourist.tourId === 'object' ? editingTourist.tourId.id : editingTourist.tourId;
                const points = getPickupPointsByTourId(tourIdValue);
                if (points.length === 0) return null;
                return (
                  <Form.Item
                    name="pickupPoint"
                    label="上车点"
                  >
                    <Select placeholder="请选择上车点" allowClear>
                      {points.map((p, i) => (
                        <Option key={i} value={p.name}>
                          {p.name}{p.time ? ` (${p.time})` : ''}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              })()}
            </>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<EditOutlined />}>
                更新信息
              </Button>
              <Button onClick={() => {
                setEditModalVisible(false);
                setEditingTourist(null);
                editForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 护照/证件照片查看弹窗 */}
      <Modal
        title={`${isDaytour(passportModalRecord || {}) ? '证件' : '护照'}照片 - ${passportModalRecord?.passportName || passportModalRecord?.touristName || ''}`}
        open={passportModalVisible}
        onCancel={() => {
          setPassportModalVisible(false);
          setPassportModalRecord(null);
        }}
        footer={isDaytour(passportModalRecord || {}) ? null : (
          // 一日游证件在会员系统订单详情里补传（2026-10-22 起一日游游客对销售只读）
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => {
              setPassportModalVisible(false);
              setPassportModalRecord(null);
              handleUploadPassport(passportModalRecord);
            }}
          >
            更新护照
          </Button>
        )}
        width={700}
      >
        {passportModalRecord && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Image
                src={passportModalRecord.passportPhoto.startsWith('http')
                  ? passportModalRecord.passportPhoto
                  : `${API_BASE.replace('/api', '')}${passportModalRecord.passportPhoto}`}
                alt={isDaytour(passportModalRecord) ? '证件照片' : '护照照片'}
                style={{ maxWidth: '100%', maxHeight: 500 }}
              />
            </div>
            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
                <div><strong>姓名：</strong>{passportModalRecord.passportName || '-'}</div>
                {isDaytour(passportModalRecord) ? (
                  <div>
                    <strong>证件号码：</strong>
                    {passportModalRecord.documentNumber || passportModalRecord.passportNumber || '-'}
                  </div>
                ) : (
                  <div><strong>护照号码：</strong>{passportModalRecord.passportNumber || '-'}</div>
                )}
                {isDaytour(passportModalRecord) && (
                  <div>
                    <strong>证件类型：</strong>
                    {DOCUMENT_TYPE_LABEL[passportModalRecord.documentType] || passportModalRecord.documentType || '-'}
                  </div>
                )}
                <div><strong>国籍：</strong>{passportModalRecord.nationality || '-'}</div>
                <div><strong>性别：</strong>{passportModalRecord.gender === 'M' ? '男' : passportModalRecord.gender === 'F' ? '女' : '-'}</div>
                <div><strong>出生日期：</strong>{passportModalRecord.passportBirthDate ? moment(passportModalRecord.passportBirthDate).format('YYYY-MM-DD') : '-'}</div>
                <div><strong>签发日期：</strong>{passportModalRecord.passportIssueDate ? moment(passportModalRecord.passportIssueDate).format('YYYY-MM-DD') : '-'}</div>
                <div><strong>有效期至：</strong>{passportModalRecord.passportExpiryDate ? moment(passportModalRecord.passportExpiryDate).format('YYYY-MM-DD') : '-'}</div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 上传护照/证件弹窗 */}
      <Modal
        title={
          uploadingIsDaytour
            ? `为 ${uploadingTourist?.touristName} 上传证件`
            : `为 ${uploadingTourist?.touristName} 上传护照`
        }
        open={uploadModalVisible}
        onCancel={() => {
          if (!uploadingPassport) {
            setUploadModalVisible(false);
            setUploadingTourist(null);
            setRecognizedData(null);
            setSelectedDocumentType('passport');
          }
        }}
        footer={null}
        width={600}
        maskClosable={!uploadingPassport}
        keyboard={!uploadingPassport}
      >
        <Spin spinning={uploadingPassport} tip={uploadingIsDaytour ? '正在上传并识别证件信息...' : '正在上传并识别护照信息...'}>
          {/* Daytour：证件类型选择 */}
          {uploadingIsDaytour && (
            <Form layout="vertical" style={{ marginBottom: 16 }}>
              <Form.Item label="证件类型" required style={{ marginBottom: 0 }}>
                <Select
                  value={selectedDocumentType}
                  onChange={setSelectedDocumentType}
                  style={{ width: '100%' }}
                  disabled={uploadingPassport}
                >
                  <Option value="passport">护照</Option>
                  <Option value="nz_driver_license">NZ驾照</Option>
                  <Option value="nz_senior_card">NZ老年卡</Option>
                </Select>
              </Form.Item>
            </Form>
          )}

          <Alert
            message="上传说明"
            description={
              <div>
                <p>支持格式：JPG、JPEG、PNG</p>
                <p>文件大小：不超过 10MB</p>
                <p>系统将自动使用 AI 识别{uploadingIsDaytour ? '证件' : '护照'}信息</p>
                <p>请确保照片清晰，{uploadingIsDaytour ? '证件' : '护照'}信息完整可见</p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Dragger
            name={uploadingIsDaytour ? 'document' : 'passport'}
            accept="image/jpeg,image/jpg,image/png"
            multiple={false}
            beforeUpload={uploadingIsDaytour ? handleDaytourDocumentUpload : handlePassportUpload}
            showUploadList={false}
            disabled={uploadingPassport}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {uploadingIsDaytour
                ? `点击或拖拽${DOCUMENT_TYPE_LABEL[selectedDocumentType] || '证件'}图片到此区域上传`
                : '点击或拖拽护照图片到此区域上传'
              }
            </p>
            <p className="ant-upload-hint">
              {uploadingIsDaytour
                ? `系统将自动识别${DOCUMENT_TYPE_LABEL[selectedDocumentType] || '证件'}信息，识别后需确认`
                : '系统将自动识别护照信息，包括姓名、护照号码、国籍等'
              }
            </p>
          </Dragger>

          {recognizedData && (
            <Alert
              message="识别成功"
              description={
                <div>
                  <p>{uploadingIsDaytour ? '证件' : '护照'}信息已成功识别并保存</p>
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    <li>姓名：{recognizedData.fullName}</li>
                    {uploadingIsDaytour ? (
                      <li>证件号：{recognizedData.documentNumber || recognizedData.passportNumber}</li>
                    ) : (
                      <li>护照号：{recognizedData.passportNumber}</li>
                    )}
                  </ul>
                </div>
              }
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Spin>
      </Modal>

      {/* 查看详情弹窗 */}
      <Modal
        title={`游客详情 - ${detailRecord?.passportName || detailRecord?.touristName || ''}`}
        open={detailModalVisible}
        onCancel={() => { setDetailModalVisible(false); setDetailRecord(null); }}
        footer={null}
        width={650}
      >
        {detailRecord && (
          <div>
            {detailRecord.passportPhoto && (
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <Image
                  src={detailRecord.passportPhoto.startsWith('http')
                    ? detailRecord.passportPhoto
                    : `${API_BASE.replace('/api', '')}${detailRecord.passportPhoto}`}
                  alt={isDaytour(detailRecord) ? '证件照片' : '护照照片'}
                  style={{ maxWidth: '100%', maxHeight: 400 }}
                />
              </div>
            )}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="录入姓名">{detailRecord.touristName || '-'}</Descriptions.Item>
              {!isDaytour(detailRecord) && detailRecord.passportName && detailRecord.passportName.trim().toUpperCase() !== (detailRecord.touristName || '').trim().toUpperCase() && (
                <Descriptions.Item label="护照姓名"><span style={{ color: '#ff4d4f' }}>{detailRecord.passportName}</span></Descriptions.Item>
              )}
              <Descriptions.Item label="团号">{detailRecord.tourName || '-'}</Descriptions.Item>
              <Descriptions.Item label="产品">{detailRecord.destinationName || '-'}</Descriptions.Item>
              <Descriptions.Item label="团类型">
                {isDaytour(detailRecord) ? <Tag color="orange">一日游</Tag> : <Tag color="blue">出境游</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="出团日期">{detailRecord.departureDate ? moment(detailRecord.departureDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
              <Descriptions.Item label="订单状态">
                {detailRecord.orderStatus === 'confirmed' ? <Tag color="green">已确认</Tag>
                  : detailRecord.orderStatus === 'cancelled' ? <Tag color="red">已取消</Tag>
                  : detailRecord.orderStatus === 'refunded' ? <Tag color="purple">退款</Tag>
                  : <Tag color="orange">待确认</Tag>}
              </Descriptions.Item>
              {isDaytour(detailRecord) ? (
                <>
                  <Descriptions.Item label="证件类型">{DOCUMENT_TYPE_LABEL[detailRecord.documentType] || detailRecord.documentType || '-'}</Descriptions.Item>
                  <Descriptions.Item label="证件号码">{detailRecord.documentNumber || detailRecord.passportNumber || '-'}</Descriptions.Item>
                </>
              ) : (
                <>
                  <Descriptions.Item label="护照号码">{detailRecord.passportNumber || '-'}</Descriptions.Item>
                  <Descriptions.Item label="国籍">{detailRecord.nationality ? getCountryDisplay(detailRecord.nationality) : '-'}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="性别">{detailRecord.gender === 'M' ? '男' : detailRecord.gender === 'F' ? '女' : '-'}</Descriptions.Item>
              <Descriptions.Item label="出生日期">{detailRecord.passportBirthDate ? moment(detailRecord.passportBirthDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
              {!isDaytour(detailRecord) && (
                <>
                  <Descriptions.Item label="护照签发日">{detailRecord.passportIssueDate ? moment(detailRecord.passportIssueDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
                  <Descriptions.Item label="护照有效期">{detailRecord.passportExpiryDate ? moment(detailRecord.passportExpiryDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="EKOK">{detailRecord.ekok || '-'}</Descriptions.Item>
              {isDaytour(detailRecord) && (
                <>
                  <Descriptions.Item label="联系电话">{detailRecord.phone || detailRecord.contactPhone || '-'}</Descriptions.Item>
                  <Descriptions.Item label="上车点">{detailRecord.pickupPoint || '-'}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="订单号">{detailRecord.orderNumber || '-'}</Descriptions.Item>
              <Descriptions.Item label="添加时间">{detailRecord.createdAt ? moment(detailRecord.createdAt).format('YYYY-MM-DD HH:mm') : '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 护照信息核对弹窗：识别结果必须核对/补齐必填项后才能提交 */}
      <Modal
        title={`核对护照信息 - ${uploadingTourist?.touristName || ''}`}
        open={passportConfirmVisible}
        onCancel={handleCancelPassportConfirm}
        width={860}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={handleCancelPassportConfirm}>放弃</Button>,
          <Button key="submit" type="primary" loading={confirmingPassport} onClick={handleConfirmPassportSubmit}>
            确认无误，提交
          </Button>
        ]}
      >
        <Alert
          message="请对照护照照片核对以下信息，缺失的必填项（如出生日期、有效期）必须补齐，否则无法提交。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        {passportOcrFailed && (
          <Alert message={passportOcrFailed} type="warning" showIcon style={{ marginBottom: 16 }} />
        )}
        {passportNameMismatch && (
          <Alert
            message="姓名不一致提醒"
            description={
              <span>
                录入姓名：<strong>{uploadingTourist?.touristName}</strong><br />
                {passportNameMismatch}<br />
                系统已保留录入姓名，请核实是否需要修改。
              </span>
            }
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {passportExpiryWarning && (
          <Alert
            message="护照有效期提醒"
            description={
              <span>
                护照有效期（{passportExpiryWarning.expiryDate}）距离出团日期（{passportExpiryWarning.departureDate}）
                仅剩 <strong style={{ color: '#ff4d4f' }}>{passportExpiryWarning.monthsDiff} 个月</strong>，不足6个月。
              </span>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={24}>
          <Col xs={24} md={10}>
            {passportTempImageUrl && (
              <Image src={passportTempImageUrl} alt="护照预览" style={{ width: '100%', maxHeight: 420, objectFit: 'contain' }} />
            )}
          </Col>
          <Col xs={24} md={14}>
            <Form form={passportForm} layout="vertical">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="fullName"
                    label="姓名（英文）"
                    extra={getNameFormatHint()}
                    rules={[{ required: true, message: '请输入姓名' }]}
                  >
                    <Input
                      placeholder="姓/名 (例: ZHANG/SAN)"
                      onChange={(e) => passportForm.setFieldsValue({ fullName: e.target.value.toUpperCase() })}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="passportNumber" label="护照号码" rules={[{ required: true, message: '请输入护照号码' }]}>
                    <Input onChange={(e) => passportForm.setFieldsValue({ passportNumber: e.target.value.toUpperCase() })} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="gender" label="性别" rules={[{ required: true, message: '请选择性别' }]}>
                    <Select>
                      <Option value="M">男/Male</Option>
                      <Option value="F">女/Female</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="nationality" label="国籍" rules={[{ required: true, message: '请选择国籍' }]}>
                    <Select showSearch optionFilterProp="label">
                      {getAllCountries().map(c => (
                        <Option key={c.value} value={c.value} label={c.label}>{c.label}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="birthDate"
                    label="出生日期"
                    rules={[{ required: true, message: '请输入出生日期（OCR 未识别到时必须手动填写）' }, { validator: validateOcrDate }]}
                  >
                    <Input placeholder="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="expiryDate"
                    label="有效期至"
                    rules={[{ required: true, message: '请输入护照有效期（OCR 未识别到时必须手动填写）' }, { validator: validateOcrDate }]}
                  >
                    <Input placeholder="DD/MM/YYYY" onChange={(e) => checkConfirmExpiryWarning(e.target.value)} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="issueDate" label="签发日期" rules={[{ validator: validateOcrDate }]}>
                    <Input placeholder="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="birthPlace" label="出生地">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
}

// ── Daytour OCR 确认表单（内嵌在 Modal.confirm 中）──────────────────────────
// 使用非受控方式（直接操作 DOM input），通过 onChange 回调将值传给父级
function DaytourOcrConfirmForm({
  initialValues,
  docTypeLabel,
  hideNationality,
  hideExpiryDate,
  hideIssueDate,
  hideBirthPlace,
  hideGender,
  onChange,
}) {
  const [vals, setVals] = React.useState({ ...initialValues });

  const update = (key, value) => {
    const next = { ...vals, [key]: value };
    setVals(next);
    onChange(next);
  };

  const fieldStyle = { marginBottom: 12 };
  const labelStyle = { display: 'block', fontWeight: 500, marginBottom: 4, color: '#333' };
  const inputStyle = {
    width: '100%',
    padding: '6px 10px',
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ paddingTop: 8 }}>
      <Alert
        message={`以下为 AI 识别的${docTypeLabel}信息，请核对并修改后提交。`}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <div style={fieldStyle}>
        <label style={labelStyle}>姓名</label>
        <input
          style={inputStyle}
          defaultValue={vals.fullName}
          onChange={(e) => update('fullName', e.target.value)}
        />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>证件号码</label>
        <input
          style={inputStyle}
          defaultValue={vals.documentNumber}
          onChange={(e) => update('documentNumber', e.target.value)}
        />
      </div>

      {!hideGender && (
        <div style={fieldStyle}>
          <label style={labelStyle}>性别（M/F）</label>
          <input
            style={inputStyle}
            defaultValue={vals.gender}
            maxLength={1}
            onChange={(e) => update('gender', e.target.value.toUpperCase())}
          />
        </div>
      )}

      {!hideNationality && (
        <div style={fieldStyle}>
          <label style={labelStyle}>国籍</label>
          <input
            style={inputStyle}
            defaultValue={vals.nationality}
            onChange={(e) => update('nationality', e.target.value)}
          />
        </div>
      )}

      <div style={fieldStyle}>
        <label style={labelStyle}>出生日期（YYYY-MM-DD）</label>
        <input
          style={inputStyle}
          defaultValue={vals.birthDate}
          placeholder="YYYY-MM-DD"
          onChange={(e) => update('birthDate', e.target.value)}
        />
      </div>

      {!hideBirthPlace && (
        <div style={fieldStyle}>
          <label style={labelStyle}>出生地</label>
          <input
            style={inputStyle}
            defaultValue={vals.birthPlace}
            onChange={(e) => update('birthPlace', e.target.value)}
          />
        </div>
      )}

      {!hideIssueDate && (
        <div style={fieldStyle}>
          <label style={labelStyle}>签发日期（YYYY-MM-DD）</label>
          <input
            style={inputStyle}
            defaultValue={vals.issueDate}
            placeholder="YYYY-MM-DD"
            onChange={(e) => update('issueDate', e.target.value)}
          />
        </div>
      )}

      {!hideExpiryDate && (
        <div style={fieldStyle}>
          <label style={labelStyle}>有效期至（YYYY-MM-DD）</label>
          <input
            style={inputStyle}
            defaultValue={vals.expiryDate}
            placeholder="YYYY-MM-DD"
            onChange={(e) => update('expiryDate', e.target.value)}
          />
        </div>
      )}
    </div>
  );
}

export default SalesTourists;
